import { Booking } from '../../models/Booking'
import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'
import { ServiceListTableCell } from './ServiceListTableCell'

type Props = {
    bookings: Booking[]
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.allSettled(
        base.props.bookings.map((b) => RecordFactory.getInstance().loadBooking(b.id!))
    )
}

function render({
    base: {
        props: { bookings, dashboard, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: dashboard?.count === 1 ? `Booking request` : `Booking requests`,
                        value: (dashboard?.count ?? 0).toString(),
                    },
                    {
                        title: 'Requested amount',
                        value: currencyFormat(dashboard?.value ?? 0, true),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Service</TableHead>
                        <TableHead className="text-right font-normal">Amount</TableHead>
                        <TableHead className="font-normal">Date</TableHead>
                        <TableHead></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {bookings.map((booking) => (
                        <ClickableTableRow key={booking.id} href={`/bookings/${booking.id}`}>
                            <TableCell>
                                <ClientTableCell
                                    user={booking.client}
                                    subaccount={booking.subaccount}
                                />
                            </TableCell>
                            <TableCell>
                                <ServiceListTableCell services={booking.lines} />
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm" className="text-right">
                                    {currencyFormat(booking.amount ?? 0, true)}
                                </Paragraph>
                            </TableCell>
                            <TableCell>
                                <Paragraph className="text-sm whitespace-nowrap">
                                    {booking.date?.format('ddd, MMM D, yyyy')}
                                </Paragraph>
                            </TableCell>
                            <TableCell className="flex justify-end">
                                <Button
                                    intent="secondary"
                                    href={`/bookings/${booking.id}`}
                                    size="small"
                                >
                                    Confirm
                                </Button>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {bookings.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const PendingDashboard = usePSComponent(render, viewWillAppear)
